/**
 * Guest online booking — TEBOX design tokens (single source: `@boubou/tebox-design-system`).
 * Use for inline styles; pair with `index.css` `--tebox-*` vars and Tailwind `tebox-*` colors.
 */
import { teboxDesignSystem } from '@boubou/tebox-design-system';

export { teboxDesignSystem };
export type { StandardTableDensityMode } from '@boubou/tebox-design-system';
export { teboxType, teboxTypographyRaw, bookingBrandFontFamilyVar } from './tebox-typography-tokens';

const ds = teboxDesignSystem;

/** Flattened tokens commonly used in booking layouts (cards, chrome, menus). */
export const bookingTebox = {
  colors: {
    border: ds.colors.border.light,
    borderField: ds.colors.border.field,
    bgPrimary: ds.colors.background.primary,
    bgSecondary: ds.colors.background.secondary,
    bgHighlight: ds.colors.background.highlight,
    bgHover: ds.colors.background.hover,
    bgTertiary: ds.colors.background.tertiary,
    textPrimary: ds.colors.text.primary,
    textSecondary: ds.colors.text.secondary,
    textTertiary: ds.colors.text.tertiary,
    focusRing: ds.colors.accent.primaryDarker,
    primary: ds.colors.primary,
  },
  radius: { ...ds.borderRadius },
  shadow: { ...ds.shadows },
  spacing: {
    pageInset: ds.spacing.pageInset,
    contentGutter: ds.spacing.contentGutter,
    sectionGap: ds.spacing.sectionGap,
    stackGap: ds.spacing.stackGap,
    stackGapTight: ds.spacing.stackGapTight,
    microGap: ds.spacing.microGap,
  },
  typography: ds.typography,
  components: {
    icon: ds.components.icon,
    tabs: ds.components.tabs,
    /** Tee sheet / admin consolidated activity banners — same tokens as `FloatingToolbar` (TEBOX_DESIGN_SYSTEM.html). */
    floatingToolbar: ds.components.floatingToolbar,
  },
  /** Guest booking tee-time grid card (compact reservation tile). */
  teeTimeCard: {
    ...ds.components.teeTimeCard,
  },
  /** Guest results table row (`TeeTimeListRow`) — clock + meridiem numerals. */
  teeTimeListRow: {
    timeFontSize: '18px',
  },
} as const;

export type BookingTebox = typeof bookingTebox;

/**
 * Use TEBOX tokens inside React components. Prefer this over importing `bookingTebox` directly
 * so a future booking-level provider (e.g. club overrides) can swap implementation in one place.
 */
export function useBookingTebox(): BookingTebox {
  return bookingTebox;
}

/** Re-export: guest TeeTimeCard + admin BookingPreview share `apps/web/src/styles/booking-spots-badge-tint.ts`. */
export {
  bookingSpotsBadgeTintMixPercent,
  bookingSpotsBadgeBackground,
} from '../../../web/src/styles/booking-spots-badge-tint';

export {
  bookingBrandInfoTintMixDefaultPercent,
  bookingBrandInfoTintCss,
  bookingBrandInfoChromeBorderMixPercent,
  bookingBrandInfoChromeStyles,
} from '../../../web/src/styles/booking-brand-info-chrome';
